// ============================================================================
// TASKCOMMENTTIMELINE.JS - ACTUALIZADO CON NUEVAS CLASES CSS Y RICH TEXT EDITOR
// ============================================================================

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  MessageSquare,
  AlertCircle,
  Activity,
  Plus,
  Trash2,
  CircleDot,
  CheckCircle2,
  RotateCcw,
  User,
  Lock,
  Unlock,
  MoveRight,
} from 'lucide-react';
import CommentTimeline from './CommentTimeline';
import { resolveAvatarUrl } from './Avatar';
import CommentService from '../services/CommentService';
import { getTaskActivity } from '../services/activityApi';
import { describeActivity, formatEventDate } from '../utils/activityText';
import { useAssignableUsers } from '../services/assignableUsers';
import { sanitizeHtml, htmlToText } from '../utils/sanitizeHtml';
import { useTaskStore } from '../store/TaskStore';
import { shallow } from 'zustand/shallow';
import { useAuth } from '../auth/AuthContext';
import RichTextEditor from '../components/RichTextEditor';
import useToast from '../services/useToast';
import '../styles/task-comment-timeline.css';

// Mapa iconKey (de describeActivity) → icono lucide del círculo del evento.
const ACTIVITY_ICONS = {
  created: Plus,
  deleted: Trash2,
  status: CircleDot,
  validated: CheckCircle2,
  unvalidated: RotateCcw,
  assigned: User,
  blocked: Lock,
  unblocked: Unlock,
  moved: MoveRight,
  generic: Activity,
};

// Reutilizable fuera de tareas (ficha de creatividad): `commentService` cambia
// el backend (mismo contrato que CommentService), `showActivity={false}` deja
// solo la pestaña Comentarios y `uploadImages={false}` evita que el editor suba
// imágenes como adjuntos de TAREA (el upload cuelga de task_id).
function TaskCommentTimeline({
  task,
  currentUser,
  onCommentChange,
  focusOnMount = false,
  commentService = CommentService,
  showActivity = true,
  uploadImages = true,
}) {
  const { showSuccess, showError } = useToast();

  // Estados locales
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  // Pestañas del centro: Comentarios (por defecto) · Actividad · Todo.
  // La actividad se carga aparte y de forma aislada: si falla, solo afecta a
  // sus pestañas, nunca a Comentarios.
  const [activeTab, setActiveTab] = useState('comentarios');
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);

  // Estados para crear comentario
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const commentFormRef = useRef(null);
  // Likes con toggle en vuelo (por comentario): ignora clics repetidos mientras la
  // petición viaja, para no solapar llamadas ni reconciliar fuera de orden.
  const likeInFlight = useRef(new Set());

  const { user, sprints } = useTaskStore(
    (s) => ({ user: s.user, sprints: s.sprints }),
    shallow
  );
  const { user: authUser } = useAuth();
  const assignableUsers = useAssignableUsers();

  // Mapas de resolución para pintar la actividad legible (id → nombre).
  // Sprints: primero los del store; como fallback, los que la tarea ya registró
  // en su historial (pueden estar cerrados y no venir en el store activo).
  const sprintNameById = useMemo(() => {
    const map = {};
    (sprints || []).forEach((s) => {
      if (s?.id) map[s.id] = s.nombre;
    });
    (task?.historialSprints || task?.historial_sprints || []).forEach((h) => {
      if (h?.sprint_id && !map[h.sprint_id]) map[h.sprint_id] = h.sprint_nombre;
    });
    return map;
  }, [sprints, task?.historialSprints, task?.historial_sprints]);

  const userById = useMemo(() => {
    const map = {};
    (assignableUsers || []).forEach((u) => {
      if (u?.id !== null && u?.id !== undefined) map[u.id] = u;
    });
    return map;
  }, [assignableUsers]);

  const resolveSprintName = (id) => sprintNameById[id] || 'otro sprint';
  const resolveUserName = (id) => userById[id]?.nombre || `usuario #${id}`;

  // Usuario activo - prioridad: currentUser > authUser > user del store
  const activeUser = currentUser || authUser || user || {
    id: null,
    nombre: 'Usuario Anónimo',
    name: 'Usuario Anónimo'
  };

  // ============================================================================
  // EFECTOS
  // ============================================================================

  useEffect(() => {
    if (task?.id) {
      loadComments();
      loadStats();
      if (showActivity) loadActivity();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id]);

  // Enfoca el editor de comentario al llegar desde "Añadir bloqueo y comentario".
  // Se espera un poco a que el timeline monte y haga scroll de la vista.
  useEffect(() => {
    if (!focusOnMount || !task?.id) return;
    const t = setTimeout(() => focusCommentEditor(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusOnMount, task?.id]);

  // ============================================================================
  // FUNCIONES DE API
  // ============================================================================

  const loadComments = async () => {
    if (!task?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      console.log('Cargando comentarios para tarea:', task.id);
      const result = await commentService.getTaskComments(task.id);

      console.log('Respuesta del servidor:', result);

      if (result.success) {
        const formattedComments = commentService.formatCommentsForFrontend(result.data);
        console.log('Comentarios formateados:', formattedComments);
        setComments(formattedComments);
        console.log('Comentarios cargados:', formattedComments.length);
      } else {
        setError(result.error || 'Error al cargar comentarios');
        setComments([]);
      }
    } catch (error) {
      setError('Error de conexión al cargar comentarios');
      setComments([]);
      console.error('Error cargando comentarios:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    if (!task?.id) return;

    try {
      const result = await commentService.getCommentStats(task.id);
      if (result.success) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  // Carga aislada del log de actividad. Un fallo aquí solo pinta el estado de
  // error de las pestañas Actividad/Todo; Comentarios sigue intacto.
  const loadActivity = async () => {
    if (!task?.id) return;

    setActivityLoading(true);
    setActivityError(null);

    try {
      const result = await getTaskActivity(task.id);
      if (result.success) {
        setActivity(result.data);
      } else {
        setActivity([]);
        setActivityError(result.error || 'No se pudo cargar la actividad');
      }
    } catch (error) {
      setActivity([]);
      setActivityError('Error de conexión al cargar la actividad');
    } finally {
      setActivityLoading(false);
    }
  };

  // ============================================================================
  // FUNCIÓN PRINCIPAL: AÑADIR COMENTARIO CON SOPORTE PARA PARENT_ID
  // ============================================================================

  const handleAddComment = async (commentText, parentId = null) => {
    if (!task?.id || !commentText?.trim()) {
      return { success: false, error: 'Datos incompletos' };
    }

    setIsSubmitting(true);

    try {
      console.log('=== AÑADIENDO COMENTARIO ===');
      console.log('Texto:', commentText);
      console.log('Parent ID:', parentId);
      console.log('Task ID:', task.id);
      console.log('Usuario activo:', activeUser);

      // Formatear datos para el backend
      const commentData = {
        tarea_id: task.id,
        usuario_id: activeUser.id,
        usuario_nombre: activeUser.nombre || activeUser.name,
        contenido: commentText.trim(),
        parent_id: parentId // CLAVE: Pasar parent_id directamente
      };

      console.log('Datos a enviar:', commentData);

      const result = await commentService.createComment(commentData);

      if (result.success) {
        console.log('Comentario creado exitosamente');
        showSuccess('Comentario enviado correctamente');
        
        // CRUCIAL: Esperar a que ambas operaciones terminen
        await Promise.all([
          loadComments(),
          loadStats()
        ]);
        
        console.log('Comentarios recargados');
        
        // Notificar al componente padre
        if (onCommentChange) {
          onCommentChange('created', result.data);
        }

        // Limpiar formulario principal si no es respuesta
        if (!parentId) {
          setNewComment('');
        }

        return { success: true };
      } else {
        console.error('Error del servidor:', result.error);
        throw new Error(result.error || 'Error desconocido al crear comentario');
      }
    } catch (error) {
      console.error('Error añadiendo comentario:', error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditComment = async (commentId, newText) => {
    if (!commentId || !newText?.trim()) {
      return { success: false, error: 'Datos incompletos' };
    }

    try {
      console.log('Editando comentario:', commentId);

      const result = await commentService.updateComment(commentId, {
        contenido: newText.trim(),
        usuario_id: activeUser.id
      });

      if (result.success) {
        console.log('Comentario editado exitosamente');
        showSuccess('Comentario editado correctamente');
        
        await Promise.all([
          loadComments(),
          loadStats()
        ]);
        
        if (onCommentChange) {
          onCommentChange('updated', result.data);
        }

        return { success: true };
      } else {
        throw new Error(result.error || 'Error al editar comentario');
      }
    } catch (error) {
      console.error('Error editando comentario:', error);
      throw error;
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!commentId) {
      showError('ID de comentario requerido');
      return { success: false, error: 'ID de comentario requerido' };
    }

    try {
      console.log('Eliminando comentario:', commentId);

      const result = await commentService.deleteComment(commentId, activeUser.id);

      if (result.success) {
        console.log('Comentario eliminado exitosamente');
        showSuccess('Comentario eliminado correctamente');

        await Promise.all([
          loadComments(),
          loadStats()
        ]);

        if (onCommentChange) {
          onCommentChange('deleted', { id: commentId });
        }

        return { success: true };
      } else {
        const errorMsg = result.error || 'Error al eliminar comentario';
        showError(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error('Error eliminando comentario:', error);
      showError(`Error al eliminar comentario: ${error.message}`);
      return { success: false, error: error.message };
    }
  };

  const handleSubmitMainComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      await handleAddComment(newComment.trim());
    } catch (error) {
      showError(`Error al enviar comentario: ${error.message}`);
    }
  };

  // Responder = modelo plano estilo Jira: en vez de anidar, anteponemos una
  // mención @autor al inicio del comentario. El chip usa el MISMO formato que
  // el autocompletado @ del editor (<span class="mention" data-user-id="ID">),
  // así el backend la detecta y notifica SOLO a esa persona.
  // Me gusta (corazón): persiste vía API y reconcilia el estado autoritativo del
  // backend. Antes NO se pasaba a CommentTimeline → el like solo se pintaba en un
  // Set local y nunca se guardaba ni contaba (bug histórico de likes).
  const handleToggleLike = async (commentId) => {
    const uid = activeUser?.id;
    if (!uid || !commentId) return;
    if (likeInFlight.current.has(commentId)) return; // ya hay un toggle en curso
    likeInFlight.current.add(commentId);

    const before = comments.find(c => c.id === commentId);
    const same = (a, b) => String(a) === String(b);
    const myName = activeUser?.nombre || activeUser?.name || 'Tú';
    // El avatar puede no venir en currentUser (a veces es un objeto reducido):
    // caemos al usuario de auth y al del store, que sí lo llevan.
    const myAvatar = activeUser?.avatar || authUser?.avatar || user?.avatar || null;
    const myPerson = { id: uid, nombre: myName, avatar: myAvatar };

    // Optimista: reflejar el cambio en el acto (incluido el popover de personas).
    setComments(prev => prev.map(c => {
      if (c.id !== commentId) return c;
      const liked = (c.likedBy || []).some(id => same(id, uid));
      const likedBy = liked
        ? (c.likedBy || []).filter(id => !same(id, uid))
        : [...(c.likedBy || []), uid];
      const prevNames = c.likedByNames || [];
      const nameIdx = prevNames.indexOf(myName);
      const likedByNames = liked
        ? (nameIdx === -1 ? prevNames : [...prevNames.slice(0, nameIdx), ...prevNames.slice(nameIdx + 1)])
        : [...prevNames, myName];
      const prevPeople = c.likedByPeople || [];
      let likedByPeople;
      if (liked) {
        // Quitar SOLO una entrada: la mía por id, o si no (fallback con ids
        // sintéticos) la primera que coincida por nombre. No borrar homónimos.
        let removed = false;
        likedByPeople = prevPeople.filter(p => {
          if (!removed && (same(p.id, uid) || p.nombre === myName)) { removed = true; return false; }
          return true;
        });
      } else {
        likedByPeople = [...prevPeople, myPerson];
      }
      const likesCount = Math.max(0, (parseInt(c.likesCount ?? c.likes_count, 10) || 0) + (liked ? -1 : 1));
      return { ...c, likedBy, likedByNames, likedByPeople, likesCount, likes_count: likesCount, isLiked: !liked };
    }));

    try {
      const result = await commentService.toggleLike(commentId, uid);
      if (result.success) {
        // Estado autoritativo del backend. Si liked_by_people aún no llega
        // (backend pre-deploy), conservamos el optimista para no perder avatares.
        const rNames = Array.isArray(result.data.liked_by_names) ? result.data.liked_by_names : null;
        const rPeople = Array.isArray(result.data.liked_by_people) ? result.data.liked_by_people : null;
        setComments(prev => prev.map(c => c.id === commentId ? {
          ...c,
          likedBy: Array.isArray(result.data.liked_by) ? result.data.liked_by : c.likedBy,
          likedByNames: rNames || c.likedByNames,
          likedByPeople: rPeople || c.likedByPeople,
          likesCount: result.data.likes_count,
          likes_count: result.data.likes_count,
          isLiked: !!result.data.is_liked,
        } : c));
      } else {
        if (before) setComments(prev => prev.map(c => c.id === commentId ? before : c));
        showError(result.error || 'No se pudo actualizar el me gusta');
      }
    } catch (error) {
      console.error('Error al procesar like:', error);
      if (before) setComments(prev => prev.map(c => c.id === commentId ? before : c));
      showError('No se pudo actualizar el me gusta');
    } finally {
      likeInFlight.current.delete(commentId);
    }
  };

  const handleStartReply = (comment) => {
    const uid = comment?.userId;
    const nombre = comment?.autor || 'Usuario';
    const hasValidId = uid != null && /^\d+$/.test(String(uid));
    const mentionHtml = hasValidId
      ? `<span class="mention" data-user-id="${uid}" contenteditable="false">@${nombre}</span>&nbsp;`
      : '';

    setNewComment((prev) => {
      const current = prev || '';
      // No duplicar si ya hay una mención a ese mismo usuario en el borrador.
      if (hasValidId && current.includes(`data-user-id="${uid}"`)) {
        return current;
      }
      return mentionHtml + current;
    });

    setTimeout(() => focusCommentEditor(), 60);
  };

  // Lleva el foco al editor de comentario (scroll + caret al final). Reutilizado
  // por "Responder" y por la llegada desde "Añadir bloqueo y comentario".
  const focusCommentEditor = () => {
    commentFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const editor = commentFormRef.current?.querySelector('[contenteditable="true"]');
    if (!editor) return;
    editor.focus();
    // Caret al final del contenido (justo tras el chip + espacio si lo hay).
    try {
      const sel = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    } catch (e) {
      /* no-op: si falla el posicionamiento del caret, el foco basta */
    }
  };

  const handleRetry = () => {
    setError(null);
    loadComments();
    loadStats();
  };

  // Función para extraer texto plano del HTML del rich editor
  const getPlainText = (html) => htmlToText(html);

  // Función para generar color de avatar basado en el nombre de usuario
  const getUserAvatarColor = (userName) => {
    if (!userName) return 'tcl-user-color-0';
    let hash = 0;
    for (let i = 0; i < userName.length; i++) {
      hash = userName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return `tcl-user-color-${Math.abs(hash) % 10}`;
  };

  // ============================================================================
  // RENDER DE LA ACTIVIDAD (pestañas Actividad / Todo)
  // ============================================================================
  // Feed compacto de solo lectura (como la sección 3 del mockup): círculo con el
  // icono de la acción + "Quién hizo qué" + fecha. Las interacciones completas
  // (editar/borrar/responder/likes) viven solo en la pestaña Comentarios.

  const renderActivityRow = (item, key) => {
    const desc = describeActivity(item, { resolveSprintName, resolveUserName });
    const Icon = ACTIVITY_ICONS[desc.iconKey] || Activity;
    return (
      <div className="tcl-event" key={key}>
        <div className={`tcl-event-icon tcl-event-icon-${desc.tone}`}>
          <Icon size={13} />
        </div>
        <div className="tcl-event-body">
          <div className="tcl-event-text">
            <b>{item.usuario_nombre || 'Sistema'}</b>{' '}
            {desc.segments.map((seg, i) =>
              seg.strong ? (
                <b className="tcl-event-strong" key={i}>{seg.text}</b>
              ) : (
                <span key={i}>{seg.text}</span>
              )
            )}
          </div>
          <div className="tcl-event-time">{formatEventDate(item.fecha_accion)}</div>
        </div>
      </div>
    );
  };

  const renderCommentEventRow = (comment, key) => (
    <div className="tcl-event" key={key}>
      <div className="tcl-event-icon tcl-event-icon-comment">
        <MessageSquare size={12} />
      </div>
      <div className="tcl-event-body">
        <div className="tcl-event-text">
          <b>{comment.autor}</b> comentó
        </div>
        <div
          className="tcl-event-comment"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(comment.contenido || '') }}
        />
        <div className="tcl-event-time">{formatEventDate(comment.fecha || comment.fechaCreacion)}</div>
      </div>
    </div>
  );

  // Estado de error/carga/vacío reutilizado por Actividad y (para su parte de
  // actividad) por Todo.
  const renderActivityStatus = () => {
    if (activityLoading) {
      return (
        <div className="tcl-loading-container">
          <div className="tcl-loading-spinner"></div>
          <span>Cargando actividad...</span>
        </div>
      );
    }
    if (activityError) {
      return (
        <div className="tcl-activity-error">
          <AlertCircle size={16} />
          <span>{activityError}</span>
          <button className="tcl-retry-button" onClick={loadActivity}>Reintentar</button>
        </div>
      );
    }
    return null;
  };

  const renderActivityTab = () => {
    const status = renderActivityStatus();
    if (status) return status;
    if (activity.length === 0) {
      return <div className="tcl-feed-empty">Sin actividad registrada todavía.</div>;
    }
    return (
      <div className="tcl-event-feed">
        {activity.map((item, i) => renderActivityRow(item, `act-${i}`))}
      </div>
    );
  };

  const renderTodoTab = () => {
    // Merge cronológico descendente de comentarios + actividad.
    const events = [
      ...comments.map((c) => ({ kind: 'comment', date: c.fecha || c.fechaCreacion, data: c })),
      ...activity.map((a) => ({ kind: 'activity', date: a.fecha_accion, data: a })),
    ].sort((a, b) => {
      const da = new Date(String(a.date || '').replace(' ', 'T')).getTime() || 0;
      const db = new Date(String(b.date || '').replace(' ', 'T')).getTime() || 0;
      return db - da;
    });

    return (
      <>
        {activityError && (
          <div className="tcl-activity-error tcl-activity-error-inline">
            <AlertCircle size={16} />
            <span>No se pudo cargar la actividad; se muestran solo los comentarios.</span>
            <button className="tcl-retry-button" onClick={loadActivity}>Reintentar</button>
          </div>
        )}
        {events.length === 0 && !activityLoading ? (
          <div className="tcl-feed-empty">Nada por aquí todavía.</div>
        ) : (
          <div className="tcl-event-feed">
            {events.map((ev, i) =>
              ev.kind === 'comment'
                ? renderCommentEventRow(ev.data, `todo-c-${ev.data.id || i}`)
                : renderActivityRow(ev.data, `todo-a-${i}`)
            )}
          </div>
        )}
      </>
    );
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!task) {
    return (
      <div className="task-comment-timeline-container">
        <div className="tcl-no-task-message">
          <MessageSquare size={24} />
          <p>Selecciona una tarea para ver sus comentarios</p>
        </div>
      </div>
    );
  }

  const commentForm = (
    <div className="tcl-main-comment-form tcl-main-comment-form-inline" ref={commentFormRef}>
      <form onSubmit={handleSubmitMainComment} className="tcl-inline-form">
        <div className={`tcl-user-avatar tcl-inline-avatar ${getUserAvatarColor(activeUser.nombre || activeUser.name)} ${activeUser.avatar ? 'tcl-avatar-has-photo' : ''}`}>
          {(activeUser.nombre?.charAt(0) || activeUser.name?.charAt(0) || 'U').toUpperCase()}
          {activeUser.avatar && (
            <img
              src={resolveAvatarUrl(activeUser.avatar)}
              alt={activeUser.nombre || activeUser.name}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
        </div>
        <div className="tcl-inline-form-body">
          <div className="tcl-rich-text-wrapper">
            <RichTextEditor
              value={newComment}
              onChange={setNewComment}
              placeholder="Escribe un comentario... Usa @ para mencionar"
              height="80px"
              readonly={isSubmitting}
              taskId={uploadImages ? task.id : null}
              enableMentions
            />
          </div>
          <div className="tcl-comment-form-actions">
            <div className="tcl-form-info">
              <span className="tcl-char-count">{getPlainText(newComment).length}/1000</span>
            </div>
            <div className="tcl-inline-form-buttons">
              {getPlainText(newComment).trim() && (
                <button
                  type="button"
                  className="tcl-cancel-button"
                  onClick={() => setNewComment('')}
                >
                  Cancelar
                </button>
              )}
              <button
                type="submit"
                className="tcl-submit-button"
                disabled={!getPlainText(newComment).trim() || isSubmitting}
              >
                {isSubmitting ? 'Enviando...' : 'Comentar'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );

  const hasComments = comments.length > 0;

  return (
    <div className="task-comment-timeline-container">

      {/* Pestañas: Comentarios (por defecto) · Actividad · Todo.
          Compactas: la actividad está a un clic pero no domina la vista. */}
      <div className="tcl-tabs" role="tablist" aria-label="Comentarios y actividad">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'comentarios'}
          className={`tcl-tab ${activeTab === 'comentarios' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('comentarios')}
        >
          Comentarios
          {comments.length > 0 && <span className="tcl-tab-count">{comments.length}</span>}
        </button>
        {showActivity && (<>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'actividad'}
          className={`tcl-tab ${activeTab === 'actividad' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('actividad')}
        >
          Actividad
          {activity.length > 0 && <span className="tcl-tab-count">{activity.length}</span>}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'todo'}
          className={`tcl-tab ${activeTab === 'todo' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('todo')}
        >
          Todo
          {comments.length + activity.length > 0 && (
            <span className="tcl-tab-count">{comments.length + activity.length}</span>
          )}
        </button>
        </>)}
        {activeTab === 'comentarios' && hasComments && stats && stats.unique_users > 0 && (
          <span className="tcl-header-participants">
            {stats.unique_users} {stats.unique_users === 1 ? 'participante' : 'participantes'}
          </span>
        )}
      </div>

      {/* ── PESTAÑA COMENTARIOS (por defecto) ── */}
      {activeTab === 'comentarios' && (
        <>
          {/* Formulario arriba si NO hay comentarios */}
          {!hasComments && commentForm}

          {/* Manejo de errores */}
          {error && (
            <div className="tcl-error-container">
              <AlertCircle size={20} />
              <div className="tcl-error-text">
                <span className="tcl-error-title">Error al cargar comentarios</span>
                <span className="tcl-error-message">{error}</span>
              </div>
              <button className="tcl-retry-button" onClick={handleRetry}>
                Reintentar
              </button>
            </div>
          )}

          {/* Estado de carga */}
          {isLoading && (
            <div className="tcl-loading-container">
              <div className="tcl-loading-spinner"></div>
              <span>Cargando comentarios...</span>
            </div>
          )}

          {/* Timeline de comentarios */}
          {!isLoading && !error && (
            <CommentTimeline
              comments={comments}
              currentUser={activeUser}
              onAddComment={handleAddComment}
              onEditComment={handleEditComment}
              onDeleteComment={handleDeleteComment}
              onToggleLike={handleToggleLike}
              onStartReply={handleStartReply}
              taskId={task.id}
            />
          )}

          {/* Formulario abajo si SÍ hay comentarios */}
          {hasComments && commentForm}
        </>
      )}

      {/* ── PESTAÑA ACTIVIDAD ── */}
      {activeTab === 'actividad' && renderActivityTab()}

      {/* ── PESTAÑA TODO (comentarios + actividad intercalados) ── */}
      {activeTab === 'todo' && renderTodoTab()}

      {/* Debug info en desarrollo */}
      {process.env.NODE_ENV === 'development' && (
        <details className="tcl-debug-info">
          <summary>Debug Info</summary>
          <pre>{JSON.stringify({
            taskId: task.id,
            activeUser: {
              id: activeUser.id,
              nombre: activeUser.nombre || activeUser.name
            },
            commentsCount: comments.length,
            hasError: !!error,
            isLoading
          }, null, 2)}</pre>
        </details>
      )}
    </div>
  );
}

export default TaskCommentTimeline;